import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';

/**
 * Everything the client can DO on a review screen, and the states that
 * replace those buttons once there is nothing left to do.
 *
 * Extracted from EpisodeReview 18 Sep 2026. That page had grown past the
 * point where it could be rewritten safely in one piece, which is a problem
 * in its own right: a file you cannot edit is a file that stops getting
 * fixed. The six panels below are mutually exclusive and were already the
 * largest single block on the page, so they came out first.
 *
 * Order matters and is unchanged: settled beats complete beats confirming
 * beats acting. A deliverable that has been approved must never still show
 * an Approve button, whatever else is true.
 */

export type Panel = 'idle' | 'confirm' | 'approved';
const EASE = [0.23, 1, 0.32, 1] as const;
interface ReviewActionsProps {
  status: string;
  name: string;
  version: string;
  panel: Panel;
  setPanel: (next: Panel) => void;
  settled: boolean;
  projectComplete: boolean;
  canApprove: boolean;
  canAddChanges: boolean;
  changesRequested: boolean;
  inReview: boolean;
  /** Changes already logged against THIS version — only changes the wording. */
  hasChanges: boolean;
  onConfirmApproval: () => void;
  onOpenComposer: () => void;
  onGoFiles: () => void;
}
export function ReviewActions({
  status,
  name,
  version,
  panel,
  setPanel,
  settled,
  projectComplete,
  canApprove,
  canAddChanges,
  changesRequested,
  inReview,
  hasChanges,
  onConfirmApproval,
  onOpenComposer,
  onGoFiles
}: ReviewActionsProps) {
  return <AnimatePresence mode="wait" initial={false} data-id="element-192" data-mp="2s0owqo248drt">
      {panel === 'approved' || settled && panel === 'idle' ? <motion.div key="approved" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-193" data-mp="2n61eh6xpgflo">
          <p className="flex items-center gap-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-194" data-mp="addrg7nm35bq">
            {status === 'Approved' ? 'Approved' : status}
            <CheckIcon className="h-6 w-6" strokeWidth={1} data-id="element-195" data-mp="jeu0aays2zdu" />
          </p>
          <p className="label mt-5 text-mute" data-id="element-196" data-mp="jcr2v5vaq3z3">
            {status === 'Approved' ? `${version ? `${version} approved · ` : ''}Portal has been notified.` : 'Your final files are ready to download.'}
          </p>
          <button type="button" onClick={onGoFiles} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-197" data-mp="3ru42dj2o44lm">
            Go to Files
            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-198" data-mp="3452m59nlfp5s" />
          </button>
        </motion.div> : projectComplete ? <motion.div key="complete" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-199" data-mp="31jbrjnfsczwn">
          <p className="font-display text-3xl leading-none sm:text-4xl" data-id="element-200" data-mp="2mndxqbtc47nl">Delivered</p>
          <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-201" data-mp="2mpqc6ezvvzvw">
            This project is complete. Final files are available to download.
          </p>
          <button type="button" onClick={onGoFiles} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-202" data-mp="2dthdx3nn77bc">
            Go to Files
            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-203" data-mp="1yy1iovbbt5dd" />
          </button>
        </motion.div> : panel === 'confirm' ? <motion.div key="confirm" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: EASE
    }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-204" data-mp="2jp6ptb9576wv">
          <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-205" data-mp="2ev2hank8aqto">
            Approve {version ? `version ${version}` : name}?
          </h2>
          <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-206" data-mp="20yp9k65hy3se">
            Portal will be notified and will prepare your final deliverables.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-207" data-mp="1rx9143y8sbqt">
            <button type="button" onClick={onConfirmApproval} className="label border border-ink bg-ink px-7 py-4 text-paper transition-colors duration-150 ease-out hover:bg-charcoal" data-id="element-208" data-mp="1vwhtp5hjq3ks">
              Confirm Approval
            </button>
            <button type="button" onClick={() => setPanel('idle')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-209" data-mp="1enfk17eclc6s">
              Cancel
            </button>
          </div>
        </motion.div> : canApprove || canAddChanges ? <motion.div key="actions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-210" data-mp="1jm8mm4s3jis6">
          <div className="flex flex-wrap gap-5" data-id="element-211" data-mp="1tdmt4wigtiut">
            {canApprove && !projectComplete && !settled && <button type="button" onClick={() => setPanel('confirm')} className="label flex-1 border border-ink bg-ink px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-charcoal sm:flex-none" data-id="element-212" data-mp="27bjs1zke61cn">
                Approve
              </button>}
            {canAddChanges && <button type="button" onClick={onOpenComposer} className="label flex-1 border border-ink px-8 py-5 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper sm:flex-none" data-id="element-213" data-mp="28203vn8sysha">
                {hasChanges ? 'Add Another Change' : 'Request Changes'}
              </button>}
          </div>

          {changesRequested && <p className="label mt-6 text-mute" data-id="element-214" data-mp="2qzkogi4jb4w3">Changes requested · Portal has been notified</p>}
        </motion.div> : inReview ? <motion.p key="viewonly" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-215" data-mp="30qfe7wgzomma">
          Your access is view only · approvals sit with your team&rsquo;s approver
        </motion.p> : <motion.p key="editing" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-216" data-mp="3eqq5h0m7o37z">
          This is with Portal · nothing to review yet
        </motion.p>}
    </AnimatePresence>;
}